import { useCallback, useEffect, useRef, useState } from 'react';
import {
  currencySettingsSchema,
  invoiceSchema,
  lineItemSchema,
  metafieldSchema,
  occurrenceEconomicsSchema,
  type Invoice,
  type InvoiceStatus,
  type Metafield,
  type SaveInvoiceRequest,
} from '@forecastify/shared';
import { useT } from '../../i18n';
import { CONTRACT_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { parseList } from '../permissions-hook';
import { lineItemContractOptions } from './contract-label';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C57 · Everything both invoice screens read and write, once.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The workspace list and the project tab are the same data narrowed by one query parameter**,
 * so the reads, the four writes and the *close the source afterwards* follow-up live here rather
 * than being transcribed into each screen — which is how two lists end up disagreeing about what a
 * `void` costs.
 *
 * ⚠️⚠️ **31 Oct 2026 · THE LISTS ARE NO LONGER READ HERE.** This read every invoice in the workspace
 * and each screen narrowed it. The two lists now page on the server (`InvoiceList`) and re-read on
 * `revision`, which every write and every live change moves; the invoice page reads its one invoice
 * (`GET /invoices/:ref`). What is still read here is ONE contract's invoices, for the contract
 * screens' line item and period — bounded by the contract.
 */

export interface InvoiceData {
  /** `contractId`'s invoices, both arms. Empty without one. */
  readonly invoices: readonly Invoice[];
  /** Moves on every write and every live change — what a list re-reads on. */
  readonly revision: number;
  readonly currencies: readonly {
    readonly code: string;
    readonly symbol: string;
    readonly rateToBase: string;
    /** 3 Nov 2026 · the workspace's base — what a new invoice starts in (`base-currency.ts`). */
    readonly isDefault: boolean;
  }[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  readonly metafields: readonly Metafield[];
  readonly lineItems: readonly { readonly id: string; readonly name: string }[];
  readonly contracts: readonly {
    readonly id: string;
    readonly code: string;
    readonly hint?: string;
  }[];
  readonly serverError: string | null;
  readonly reload: () => Promise<void>;
  readonly save: (
    id: string | null,
    body: SaveInvoiceRequest,
    closeSource: boolean,
  ) => Promise<Invoice | null>;
  readonly setStatus: (invoice: Invoice, status: InvoiceStatus) => Promise<void>;
  readonly remove: (invoice: Invoice) => Promise<void>;
  readonly patch: (invoice: Invoice, change: InvoicePatch) => Promise<void>;
  readonly periodsOf: (
    contractId: string,
  ) => Promise<readonly { startsOn: string; label: string }[]>;
  readonly clearError: () => void;
}

/**
 * What the invoice detail edits in place — ux-pass-2, 14 Oct: *"This should be inline like all other
 * parts … no edit button needed."*
 */
export interface InvoicePatch {
  readonly externalId?: string;
  readonly amount?: number;
  readonly issuedOn?: string;
  readonly ownerMemberId?: string;
  readonly noteHtml?: string;
  readonly metafields?: Record<string, unknown>;
}

export function useInvoices(
  options: {
    /** The project tab's — narrows the dialog's line item and contract pickers. */
    readonly projectId?: string;
    /** The contract screens' — whose invoices `invoices` holds. */
    readonly contractId?: string | null;
  } = {},
): InvoiceData {
  const t = useT();
  const session = useSession();
  const { projectId } = options;
  const contractId = options.contractId ?? null;

  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [revision, setRevision] = useState(0);
  const [currencies, setCurrencies] = useState<
    readonly { code: string; symbol: string; rateToBase: string; isDefault: boolean }[]
  >([]);
  const [members, setMembers] = useState<readonly { id: string; name: string }[]>([]);
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  const [lineItems, setLineItems] = useState<
    readonly { id: string; name: string; closed: boolean; projectId: string | null }[]
  >([]);
  const [contracts, setContracts] = useState<
    readonly { id: string; code: string; hint: string; projectId: string | null }[]
  >([]);
  const [serverError, setServerError] = useState<string | null>(null);

  /* ⚠️ The session through a ref: `reload` moves `revision`, and a callback rebuilt with a fresh
     session object would re-run the effect below on the render that move causes. */
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const workspaceId = session.workspaceId;
  const readContract = useCallback(async (): Promise<void> => {
    if (workspaceId === null) return;
    if (contractId === null) {
      setInvoices([]);
      return;
    }
    const response = await requestAll(
      sessionRef.current,
      `/invoices?contractId=${contractId}`,
    ).catch(() => null);
    if (response?.ok !== true) return;
    setInvoices(parseList(invoiceSchema, await response.json()) ?? []);
  }, [workspaceId, contractId]);

  useEffect(() => {
    void readContract();
  }, [readContract]);

  /* ⚠️ `revision` moves HERE and not on the first read, so a list mounting beside this hook reads
     its page once rather than once more when the mount's read lands. */
  const reload = useCallback(async (): Promise<void> => {
    setRevision((current) => current + 1);
    await readContract();
  }, [readContract]);

  useLiveInvalidation(CONTRACT_TOPICS, () => {
    void reload();
  });

  /* ⚠️ The pickers, once. They do not depend on the list, and re-reading every line item each time
     somebody typed in the search would be four requests for a dropdown. */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const [itemResponse, currencyResponse, memberResponse, fieldResponse] = await Promise.all([
        session.request('/line-items').catch(() => null),
        session.request('/workspace/currencies').catch(() => null),
        requestAll(session, '/people').catch(() => null),
        session.request('/metafields?entity=invoice').catch(() => null),
      ]);

      if (itemResponse?.ok === true) {
        const parsed = parseList(lineItemSchema, await itemResponse.json()) ?? [];
        const mine =
          projectId === undefined ? parsed : parsed.filter((one) => one.projectId === projectId);
        /* ⚠️ U-6 · a recurring contract is invoiced by PERIOD, so its line items are no target —
           the server refuses one with `invoice_by_period`, and the picker does not offer it. */
        /* R1/R3 · `closed` and the project travel: the dialog offers no closed line item but the
           one an invoice already sits on, and its owners come from the source's project. */
        setLineItems(
          mine
            .filter((one) => !one.contractRecurring)
            .map((one) => ({
              id: one.id,
              name: one.name,
              closed: one.status === 'closed',
              projectId: one.projectId,
            })),
        );
        /* ⚠️ The contracts derived from the line items rather than read again — an invoice's
           occurrence arm can only point at a contract that has deliverables in scope here. */
        /* 3 Nov 2026 · named, the identifier beside it (`contractOptions`). */
        const projectOf = new Map(mine.map((one) => [one.contractId, one.projectId]));
        setContracts(
          lineItemContractOptions(mine).map((one) => ({
            id: one.value,
            code: one.label,
            hint: one.hint,
            projectId: projectOf.get(one.value) ?? null,
          })),
        );
      }

      if (currencyResponse?.ok === true) {
        const parsed = currencySettingsSchema.safeParse(await currencyResponse.json());
        if (parsed.success) {
          setCurrencies(
            parsed.data.currencies
              .filter((one) => one.active)
              .map((one) => ({
                code: one.code,
                symbol: one.symbol,
                rateToBase: one.rateToBase,
                isDefault: one.isDefault,
              })),
          );
        }
      }

      if (memberResponse?.ok === true) {
        const body = (await memberResponse.json()) as { data?: unknown };
        if (Array.isArray(body.data)) {
          setMembers(
            body.data
              .filter(
                (one): one is { id: string; displayName: string } =>
                  typeof one === 'object' &&
                  one !== null &&
                  typeof (one as { id?: unknown }).id === 'string',
              )
              .map((one) => ({ id: one.id, name: one.displayName })),
          );
        }
      }

      if (fieldResponse?.ok === true) {
        setMetafields(parseList(metafieldSchema, await fieldResponse.json()) ?? []);
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId, projectId]);

  const periodsOf = useCallback(
    async (contractId: string): Promise<readonly { startsOn: string; label: string }[]> => {
      if (contractId === '' || session.workspaceId === null) return [];
      const response = await session
        .request(`/contracts/${contractId}/economics`)
        .catch(() => null);
      if (response?.ok !== true) return [];
      const parsed = parseList(occurrenceEconomicsSchema, await response.json()) ?? [];
      return parsed.map((one) => ({
        startsOn: one.startsOn,
        label: `#${String(one.index)} · ${one.startsOn}`,
      }));
    },
    [session],
  );

  /**
   * ⚠️⚠️ **A REFUSAL NAMES ITS FIGURE, AND THAT IS THE WHOLE REASON FOR READING THE BODY.** The
   * server answers `invoice_exceeds_revenue` with the remaining amount in `details`; showing
   * *"something went wrong"* instead would leave somebody guessing at a number the server has just
   * computed for them.
   */
  const write = useCallback(
    async (path: string, init: RequestInit): Promise<Response | null> => {
      setServerError(null);
      const response = await session.request(path, init).catch(() => null);
      if (response === null) {
        setServerError(t('error.unavailable'));
        return null;
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string; details?: { code?: string; message?: string }[] };
        } | null;
        const code = body?.error?.code;
        if (code === 'invoice_exceeds_revenue') {
          const amount =
            body?.error?.details?.find((one) => one.code === 'remaining')?.message ?? '';
          setServerError(t('invoice.error.overRevenue', { amount }));
        } else if (code === 'invoice_by_period') {
          /* U-6 · a line item of a recurring contract — new, or one issued before U-6. */
          setServerError(t('invoice.error.byPeriod'));
        } else if (code === 'stale_version') {
          setServerError(t('error.conflict'));
        } else if (code === 'line_item_closed') {
          /* R1 · 3 Nov 2026 · reopen the line item to add an invoice to it. */
          setServerError(
            t('error.line_item_closed', { name: body?.error?.details?.[0]?.message ?? '' }),
          );
        } else if (code === 'member_not_active') {
          setServerError(
            t('error.member_not_active', { name: body?.error?.details?.[0]?.message ?? '' }),
          );
        } else if (code === 'assignee_not_a_project_member') {
          /* R3 · the owner is not a current member of the invoice's project. */
          setServerError(
            t('error.owner_not_a_project_member', {
              name: body?.error?.details?.[0]?.message ?? '',
            }),
          );
        } else {
          setServerError(t('error.unavailable'));
        }
        return null;
      }
      await reload();
      return response;
    },
    [session, t, reload],
  );

  const save = useCallback(
    async (
      id: string | null,
      body: SaveInvoiceRequest,
      closeSource: boolean,
    ): Promise<Invoice | null> => {
      const response = await write(id === null ? '/invoices' : `/invoices/${id}`, {
        method: id === null ? 'POST' : 'PUT',
        body: JSON.stringify(body),
      });
      if (response === null) return null;
      const saved = invoiceSchema.safeParse(await response.json());

      /**
       * ⚠️ **Closing the source is a SECOND write and deliberately not part of the first.** An
       * invoice that saved and a line item that did not close is an ordinary partial outcome
       * somebody can finish by hand; a transaction spanning the two would make the invoice fail
       * because a deliverable was already closed, which is not a reason to refuse money.
       */
      if (closeSource) {
        if (body.targetKind === 'line_item') {
          await session
            .request(`/line-items/${body.lineItemId}`, {
              method: 'PATCH',
              body: JSON.stringify({ status: 'closed' }),
            })
            .catch(() => null);
        } else {
          await session
            .request(`/contracts/${body.contractId}/periods`, {
              method: 'PUT',
              body: JSON.stringify({ startsOn: body.occurrenceStartsOn, open: false }),
            })
            .catch(() => null);
        }
        await reload();
      }

      return saved.success ? saved.data : null;
    },
    [write, session, reload],
  );

  const setStatus = useCallback(
    async (invoice: Invoice, status: InvoiceStatus): Promise<void> => {
      await write(`/invoices/${invoice.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, version: invoice.version }),
      });
    },
    [write],
  );

  const remove = useCallback(
    async (invoice: Invoice): Promise<void> => {
      await write(`/invoices/${invoice.id}`, { method: 'DELETE' });
    },
    [write],
  );

  /**
   * ⚠️ **A full `PUT` — the save endpoint takes the whole record, so an edit rebuilds it.**
   *
   * ⚠️ **And only the fields the detail screen edits in place** (`InvoicePatch`). The TARGET is not
   * one of them: moving it re-derives the cap and the currency from a new source, so it moves
   * through the dialog, opened from its rail row.
   */
  const patch = useCallback(
    async (invoice: Invoice, change: InvoicePatch): Promise<void> => {
      const base = {
        externalId: invoice.externalId,
        amount: Number(invoice.amount),
        issuedOn: invoice.issuedOn,
        status: invoice.status,
        /* `0210` · the HTML goes back, so an edit to the amount keeps the note's formatting. */
        noteHtml: invoice.noteHtml,
        ownerMemberId: invoice.ownerMemberId,
        version: invoice.version,
        ...change,
      };

      const body: SaveInvoiceRequest =
        invoice.targetKind === 'occurrence'
          ? {
              ...base,
              targetKind: 'occurrence',
              contractId: invoice.contractId ?? '',
              occurrenceStartsOn: invoice.occurrenceStartsOn ?? '',
            }
          : { ...base, targetKind: 'line_item', lineItemId: invoice.lineItemId ?? '' };

      await write(`/invoices/${invoice.id}`, { method: 'PUT', body: JSON.stringify(body) });
    },
    [write],
  );

  return {
    invoices,
    revision,

    currencies,
    members,
    metafields,
    lineItems,
    contracts,
    serverError,
    reload,
    save,
    setStatus,
    remove,
    patch,
    periodsOf,
    clearError: () => {
      setServerError(null);
    },
  };
}
